/**
 * Phase 2 — CategoryConfig registry.
 *
 * The single import surface for any code that needs to look up a
 * vertical's config by id. Replaces the scattered `CATEGORY_CONFIG`,
 * `SCORE_FIELDS_BY_CATEGORY`, `CATEGORY_BENCHMARKS`, `DETAILS_FIELDS`,
 * and `ADMIN_CATEGORY` maps that used to live inside individual
 * components.
 *
 * Usage:
 *   import { getCategoryConfig } from "../lib/categoryRegistry";
 *   const config = getCategoryConfig("insurance");
 *   const sortOptions = config.rateTable.sortOptions;
 *
 * Legacy fallback semantics preserved: looking up an unknown
 * category returns the auto-loan config (matching the original
 * `PartnerRateTable.getCategoryConfig` fallback at line 120).
 */
import { INSURANCE_CONFIG } from "../features/insurance/config";
import { AUTO_LOAN_CONFIG } from "../features/auto-loans/config";
import { REFINANCE_CONFIG } from "../features/refinance/config";
import { WARRANTY_CONFIG } from "../features/warranty/config";

export const CATEGORY_REGISTRY = {
  insurance: INSURANCE_CONFIG,
  "auto-loan": AUTO_LOAN_CONFIG,
  refinance: REFINANCE_CONFIG,
  warranty: WARRANTY_CONFIG,
};

export const CATEGORY_IDS = Object.keys(CATEGORY_REGISTRY);

/**
 * Look up a vertical's config by id. Returns the auto-loan config
 * as a defensive fallback so legacy callers passing unknown strings
 * (e.g. "auto-loan-new" / "auto-loan-used") keep working — those
 * sub-verticals map onto auto-loan-shaped UI by design.
 */
export function getCategoryConfig(categoryId) {
  return CATEGORY_REGISTRY[categoryId] || AUTO_LOAN_CONFIG;
}

/**
 * Build legacy-shape map from the canonical registry. Used by the
 * Phase 2 shim wrappers inside PartnerRateTable.jsx and
 * RatesPanel.jsx so they keep their existing internal access
 * pattern (lookups by string key) without re-declaring the values.
 */
export function buildLegacyShape(field) {
  return Object.fromEntries(
    Object.entries(CATEGORY_REGISTRY).map(([id, cfg]) => [id, cfg[field]]),
  );
}
